import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  AcademicCapIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  BookOpenIcon,
  UserGroupIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline';

const LearningResources = () => {
  const resources = [
    {
      title: "No Place for Hate - Anti Defamation League",
      description: "Building inclusive school communities and combating bias and hate through education and advocacy.",
      image: "/assets/images/learningresources/ADL.png",
      icon: UserGroupIcon,
      link: "https://www.adl.org/no-place-hate",
      isClickable: true
    },
    {
      title: "NYC DOE Computer Science for All",
      description: "Bringing computer science education to all students across New York City public schools.",
      image: "/assets/images/learningresources/science for all.png",
      icon: ComputerDesktopIcon,
      link: "https://infohub.nyced.org/in-our-schools/programs/computer-science-for-all-overview",
      isClickable: true
    },
    {
      title: "English Language Learners Programs and Services",
      description: "Comprehensive support and specialized programs for English Language Learners to ensure academic success and language development.",
      image: null,
      icon: GlobeAltIcon,
      link: "https://infohub.nyced.org/in-our-schools/programs/english-language-learners-programs-and-services",
      isClickable: true
    },
    {
      title: "NYC DOE Bronx District 9",
      description: "Our district office providing leadership, support, and resources for schools in the Highbridge community.",
      image: "/assets/images/learningresources/district9.png",
      icon: BuildingLibraryIcon,
      link: "https://bronxdistrict9.org/",
      isClickable: true
    },
    {
      title: "New York City Public Schools",
      description: "The largest public school system in the United States, serving over one million students.",
      image: "/assets/images/learningresources/new york public library.png",
      icon: BookOpenIcon,
      link: "https://www.schools.nyc.gov/",
      isClickable: true
    },
    {
      title: "The United Federation of Teachers - Teacher Center",
      description: "Professional development and support resources for educators to enhance teaching practices and student outcomes.",
      image: "/assets/images/learningresources/uft.png",
      icon: AcademicCapIcon,
      link: "https://www.uft.org/your-union/uft-programs/uft-teacher-center",
      isClickable: true
    }
  ];

  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Academics
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Learning <span className="font-bold">Resources</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Comprehensive educational resources and partnerships that support our students, families, and educators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Educational <span className="font-bold text-[#630503]">Partners & Resources</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These valuable resources and partnerships enhance our educational programs and support our school community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource, index) => {
              const IconComponent = resource.icon;
              const content = (
                <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden h-full transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
                  
                  {/* Image or Icon */}
                  <div className="mb-6 text-center">
                    {resource.image ? (
                      <div className="w-24 h-24 mx-auto mb-4 rounded-lg overflow-hidden shadow-md bg-gray-50 flex items-center justify-center">
                        <img 
                          src={resource.image} 
                          alt={resource.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-[#630503]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="h-8 w-8 text-[#630503]" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 leading-tight">
                      {resource.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {resource.description}
                    </p>
                    
                    {resource.isClickable && (
                      <div className="mt-4 flex justify-center">
                        <span className="inline-flex items-center text-[#630503] text-sm font-medium">
                          Click to visit
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );

              if (resource.isClickable && resource.link) {
                return (
                  <a 
                    key={index}
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    {content}
                  </a>
                );
              }

              return (
                <div key={index}>
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Academics*/}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Our <span className="font-bold text-[#630503]">Academics</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              At P.S. 11X, we ensure that all students receive the best education possible. Our programs are aligned to
              the New York State Learning Standards and provide multiple entry points so that every learner can thrive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#630503]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#630503] text-white flex items-center justify-center font-bold text-lg shadow-md">R</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Reading</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                Our literacy program is HMH Into Reading, a research-based program that develops a culture of learning and growth.
                Students engage with rich texts and purposeful practice to build strong reading foundations.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#8B4513]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] via-[#A0522D] to-[#630503]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#8B4513] text-white flex items-center justify-center font-bold text-lg shadow-md">W</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Writing</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                Writing instruction is also through HMH Into Reading. Scholars write in the three major genres throughout the year:
                narrative, opinion, and informational writing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#A0522D]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A0522D] via-[#8B4513] to-[#630503]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#A0522D] text-white flex items-center justify-center font-bold text-lg shadow-md">M</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mathematics</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                Eureka Math²™ is a revolutionary math program designed to ensure that students move beyond rote memorization to build enduring math knowledge.
                Its learning design allows your student to build a deep conceptual understanding of mathematics that will better prepare them for college, career, and beyond.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#630503]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#630503] text-white flex items-center justify-center font-bold text-lg shadow-md">S</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Science</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                Students are engaged throughout the year with Amplify Science. The curriculum provides hands‑on activities and
                investigations through a rigorous program aligned to the Next Generation Science Standards.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#8B4513]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] via-[#A0522D] to-[#630503]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#8B4513] text-white flex items-center justify-center font-bold text-lg shadow-md">SS</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Social Studies</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                We use the NYC Passport to Social Studies—a curriculum designed to provide authentic, hands‑on experiences with
                primary sources while promoting critical thinking skills.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-[#A0522D]/5 rounded-full blur-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A0522D] via-[#8B4513] to-[#630503]"></div>
              <div className="mb-4">
                <div className="w-12 h-12 rounded-full bg-[#A0522D] text-white flex items-center justify-center font-bold text-lg shadow-md">PE</div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Physical Education</h3>
              <div className="w-12 h-0.5 bg-[#630503]/40 mb-4"></div>
              <p className="text-gray-700 leading-relaxed">
                Students participate in physical and health education several times a week. We promote whole‑body wellness by
                teaching the essentials for maintaining healthy bodies inside and out.
              </p>
            </div>
          </div>

          {/* Student Council */}
          <div className="mt-16">
            <div className="bg-[#630503]/5 rounded-xl p-8 lg:p-12 shadow-xl border border-[#630503]/20">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Student Council</h3>
                <div className="w-16 h-px bg-[#630503]/40 mx-auto"></div>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed text-center max-w-3xl mx-auto">
                Student Council is the voice of the students at P.S. 11X. Students meet monthly with school leadership to
                discuss ways to improve the school and build a stronger community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#630503]/5 rounded-xl p-8 lg:p-12 shadow-xl border border-[#630503]/20">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                Supporting Student Success
              </h2>
              <div className="w-16 h-px bg-[#630503]/40 mx-auto"></div>
            </div>
            <div className="prose prose-lg max-w-none text-center">
              <p className="text-lg text-gray-700 leading-relaxed">
                These learning resources represent our commitment to providing comprehensive educational support. 
                Through partnerships with leading organizations and access to specialized programs, we ensure that 
                every student at P.S. 11X has the tools and opportunities they need to succeed academically and 
                develop into well-rounded individuals.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default LearningResources;
